import { useCallback } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useLanguage } from "@/hooks/useLanguage"

const CATEGORIES = [
    {
        id: 1,
        key: "beef",
        image: "/images/categories/beef.png",
        count: 142,
    },
    {
        id: 2,
        key: "pork",
        image: "/images/categories/pork.png",
        count: 85,
    },
    {
        id: 3,
        key: "poultry",
        image: "/images/categories/poultry.png",
        count: 64,
    },
    {
        id: 4,
        key: "lamb",
        image: "/images/categories/lamb.png",
        count: 32,
    },
    {
        id: 5,
        key: "specialty",
        image: "/images/categories/specialty.png",
        count: 18,
    },
]

export const FeaturedCategories = () => {
    const { t } = useLanguage()
    const text = t.featuredCategories
    const navigate = useNavigate()
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "start",
        loop: true,
        skipSnaps: false,
        dragFree: true,
    })

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

    return (
        <section className="relative overflow-hidden bg-background py-16">
            {/* Background Industrial Accents */}
            <div className="absolute left-0 top-0 h-4 w-full bg-[#1b1e1a] bg-[linear-gradient(45deg,#000_25%,transparent_25%,transparent_75%,#000_75%,#000)] bg-[length:24px_24px] opacity-[0.03]" />
            <div className="absolute -left-12 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-primary/5 mix-blend-multiply blur-3xl" />

            <div className="container mx-auto px-4">
                <div className="mb-12 flex items-end justify-between">
                    <div className="max-w-2xl">
                        <motion.h2
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="font-display text-4xl tracking-widest text-foreground md:text-5xl"
                        >
                            {text.title}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="mt-2 text-lg text-muted-foreground"
                        >
                            {text.subtitle}
                        </motion.p>
                    </div>

                    <div className="hidden items-center gap-4 md:flex">
                        <button
                            onClick={scrollPrev}
                            className="group flex h-12 w-12 items-center justify-center border-2 border-foreground/10 bg-transparent transition-all hover:border-primary hover:bg-primary"
                        >
                            <ChevronLeft className="h-5 w-5 text-foreground/50 transition-colors group-hover:text-primary-foreground" />
                        </button>
                        <button
                            onClick={scrollNext}
                            className="group flex h-12 w-12 items-center justify-center border-2 border-foreground/10 bg-transparent transition-all hover:border-primary hover:bg-primary"
                        >
                            <ChevronRight className="h-5 w-5 text-foreground/50 transition-colors group-hover:text-primary-foreground" />
                        </button>
                    </div>
                </div>

                <div className="overflow-hidden" ref={emblaRef}>
                    <div className="flex touch-pan-y pb-12 pt-4">
                        {CATEGORIES.map((category, index) => (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                key={category.id}
                                className="min-w-[75vw] flex-[0_0_75vw] pl-6 sm:min-w-[40vw] sm:flex-[0_0_40vw] lg:min-w-[25vw] lg:flex-[0_0_25vw]"
                            >
                                <div
                                    onClick={() => navigate(`/catalog/marketplace?category=${category.id}`)}
                                    className="group relative aspect-[4/5] cursor-pointer overflow-hidden bg-muted"
                                >
                                    <img
                                        src={category.image}
                                        alt={text.items[category.key as keyof typeof text.items]}
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out-expo group-hover:scale-110"
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300" />

                                    <div className="absolute bottom-0 left-0 flex w-full flex-col justify-end p-6">
                                        <span className="mb-2 w-fit border border-white/20 bg-black/40 px-2 py-1 font-mono text-xs font-bold uppercase tracking-widest text-white backdrop-blur-md">
                                            {category.count} {text.products}
                                        </span>
                                        <h3 className="font-display text-3xl tracking-wider text-white transition-colors group-hover:text-primary lg:text-4xl">
                                            {text.items[category.key as keyof typeof text.items]}
                                        </h3>
                                    </div>

                                    <div className="absolute -bottom-6 right-6 flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground opacity-0 transition-all duration-300 group-hover:bottom-6 group-hover:opacity-100">
                                        <ChevronRight className="h-6 w-6" />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

